/**
 * Subida de documentos de empresa (Carlos jun 28, 2026)
 *
 * Tras el registro la empresa cae aquí. Debe subir 3 docs:
 *   1. Cédula del rep legal (frente + reverso)
 *   2. Certificado de vigencia
 *   3. Carpeta tributaria SII
 *
 * Cada upload llama a Gemini para autocompletar info de la empresa.
 * Cuando los 3 docs están listos, status → pending_review (admin revisa).
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import empresaApi from '../utils/empresaApi';
import { BRAND } from '../brand';

const DOC_LABELS = {
  cedula_rep_front: '📇 Cédula representante legal (FRENTE)',
  cedula_rep_back: '📇 Cédula representante legal (REVERSO)',
  vigencia_cert: '📄 Certificado de vigencia (Registro de Comercio)',
  carpeta_tributaria: '🏛️ Carpeta Tributaria SII',
};

const EmpresaDocs = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [error, setError] = useState('');
  const refs = useRef({});

  const load = async () => {
    try {
      const res = await empresaApi.get('/empresas/me');
      setMe(res.data);
      if (res.data.status === 'approved') {
        navigate('/empresas/panel');
      }
    } catch (err) {
      setError('No pude cargar tus datos. ¿Volver a iniciar sesión?');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('empresa_token')) {
      navigate('/empresas/login');
      return;
    }
    load();
  }, [navigate]);

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    setError('');
    try {
      const form = new FormData();
      form.append('doc_type', docType);
      form.append('file', file);
      await empresaApi.post('/empresas/me/upload-doc', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error subiendo el archivo');
    } finally {
      setUploading(null);
    }
  };

  const logout = () => {
    localStorage.removeItem('empresa_token');
    navigate('/empresas/login');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando…</div>;
  }

  if (!me) return null;

  const docs = me.docs || {};
  const allUploaded = ['cedula_rep_front', 'vigencia_cert', 'carpeta_tributaria'].every((k) => docs[k]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link to="/" className="text-sm text-emerald-700 hover:underline">← Inicio</Link>
          <button onClick={logout} className="text-xs text-slate-500 hover:text-slate-700" data-testid="emp-logout">
            Cerrar sesión
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 sm:p-8">
          <h1 className="text-2xl font-black text-slate-900 mb-1">📂 Documentación de tu empresa</h1>
          <p className="text-sm text-slate-600">
            <strong>{me.razon_social}</strong> · {me.rut} · {me.rep_legal_name}
          </p>

          {/* Estado */}
          <div className="mt-3" data-testid="emp-status-badge">
            <StatusBadge status={me.status} reason={me.rejection_reason} />
          </div>

          {/* Info extraída por IA */}
          {me.ai_extracted && Object.keys(me.ai_extracted).length > 0 && (
            <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs" data-testid="emp-ai-extracted">
              <p className="font-semibold text-purple-800 mb-1">🤖 Datos detectados por IA en tus documentos:</p>
              <div className="grid grid-cols-2 gap-1 text-purple-900">
                {Object.entries(me.ai_extracted).map(([k, v]) => (
                  <div key={k}>
                    <strong>{k.replaceAll('_', ' ')}:</strong> {String(v)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lista de docs */}
          <div className="mt-6 space-y-3">
            {Object.entries(DOC_LABELS).map(([docType, label]) => {
              const isUp = !!docs[docType];
              const isOptional = docType === 'cedula_rep_back';
              return (
                <div
                  key={docType}
                  className={`border-2 rounded-xl p-3 ${isUp ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}
                  data-testid={`emp-doc-${docType}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900">
                        {label} {isOptional && <span className="text-xs text-slate-500">(opcional)</span>}
                      </p>
                      {isUp && (
                        <p className="text-xs text-emerald-700 mt-0.5">
                          ✓ Subido {docs[docType].uploaded_at?.slice(0, 10)}
                          {docs[docType].extracted && Object.keys(docs[docType].extracted).length > 0 && (
                            <span className="ml-2 text-purple-700">+ IA analizó</span>
                          )}
                        </p>
                      )}
                    </div>
                    {me.status !== 'approved' && (
                      <>
                        <input
                          ref={(el) => (refs.current[docType] = el)}
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={(e) => handleUpload(docType, e.target.files?.[0])}
                          data-testid={`emp-doc-input-${docType}`}
                        />
                        <button
                          onClick={() => refs.current[docType]?.click()}
                          disabled={uploading === docType}
                          data-testid={`emp-doc-upload-btn-${docType}`}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${isUp ? 'bg-slate-200 hover:bg-slate-300 text-slate-700' : 'bg-emerald-600 hover:bg-emerald-700 text-white'} disabled:opacity-60`}
                        >
                          {uploading === docType ? 'Subiendo + IA…' : isUp ? 'Reemplazar' : 'Subir'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3" data-testid="emp-docs-error">{error}</div>
          )}

          {allUploaded && me.status === 'pending_review' && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900" data-testid="emp-pending-review">
              <p className="font-semibold mb-1">⏳ Documentos en revisión</p>
              <p className="text-xs">
                Recibimos tus 3 documentos. El equipo de {BRAND.name} los revisará en las próximas
                horas hábiles. Te avisaremos por email cuando esté aprobado.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status, reason }) => {
  const map = {
    pending_docs: { color: 'bg-amber-100 text-amber-800 border-amber-300', label: '📤 Faltan documentos' },
    pending_review: { color: 'bg-blue-100 text-blue-800 border-blue-300', label: '🔎 En revisión' },
    approved: { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: '✅ Aprobada' },
    rejected: { color: 'bg-red-100 text-red-800 border-red-300', label: '❌ Rechazada' },
  };
  const m = map[status] || map.pending_docs;
  return (
    <div className={`inline-flex flex-col gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border ${m.color}`}>
      <span>{m.label}</span>
      {status === 'rejected' && reason && (
        <span className="font-normal italic text-xs">Motivo: {reason}</span>
      )}
    </div>
  );
};

export default EmpresaDocs;
